<script setup lang="ts">
  import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
  import { createClickOutsideListener } from '@/utils/clickOutside';
  import {
    placeDropdown,
    createRecursiveIntersectionWatcher,
    getScrollableParents,
  } from '@/utils/viewport';
  import { Option } from '@/models/interfaces';

  type AnyOption = Option<any>;

  const props = defineProps({
    index: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'search',
    },
    placeholder: {
      type: String,
      default: '',
    },
    // Should the input displays its own validity style or delegate it to the parent ?
    standalone: {
      type: Boolean,
      default: true,
    },
    // Should options be filtered on search ?
    isFilteredOnSearch: {
      type: Boolean,
      default: false,
    },
  });

  // Main input value.
  const options = defineModel<Array<AnyOption>>('options');
  const selectedOption = defineModel<AnyOption>('selected-option');
  const searchValue = defineModel<string>('search-value');
  const required = defineModel<boolean>('required');
  const disabled = defineModel<boolean>('disabled');

  const wrapperRef = ref<HTMLDivElement>();
  const searchRef = ref<HTMLInputElement>();
  const fieldsetRef = ref<HTMLFieldSetElement>();

  const isOpen = ref(false);

  /*
   * Used with requestAnimationFrame.
   * Aims to reduce the load on the scroll event performance.
   */
  let tick = false;
  let recursiveIntersectionWatcher: ReturnType<typeof createRecursiveIntersectionWatcher>;

  watch(
    () => selectedOption.value,
    () => {
      searchValue.value = selectedOption.value?.label || '';
    },
  );

  const clickListener = createClickOutsideListener(
    [`#custom-search-select-input-${props.index}`, `#custom-search-select-${props.index} fieldset`],
    () => {
      handleClose();
    },
  );

  const resizeObserver = new ResizeObserver(() => {
    placeFieldset();
  });

  onMounted(async () => {
    if (selectedOption.value?.label) searchValue.value = selectedOption.value.label;

    setTimeout(() => {
      clickListener.start();
    }, 0);

    if (searchRef.value instanceof HTMLElement) {
      getScrollableParents(searchRef.value).map((el) => {
        el.addEventListener('scroll', placeFieldsetOnFrame, { passive: true });
      });

      recursiveIntersectionWatcher = createRecursiveIntersectionWatcher(searchRef.value, {
        threshold: 0.5,
        axis: 'y',
        onEnter: (els: HTMLElement[]) => {
          els.forEach((el) => {
            el.addEventListener('scroll', placeFieldsetOnFrame, { passive: true });
          });
          return els;
        },
        onLeave: (els: HTMLElement[]) => {
          handleClose();
          els.forEach((el) => {
            el.removeEventListener('scroll', placeFieldsetOnFrame);
          });
          return els;
        },
      });
      recursiveIntersectionWatcher.start();
    }

    if (wrapperRef.value) {
      window.addEventListener('resize', placeFieldsetOnFrame, { passive: true });
      window.addEventListener('scroll', placeFieldsetOnFrame, { passive: true });

      resizeObserver.observe(wrapperRef.value);
    }
  });

  onUnmounted(() => {
    clickListener.stop();
    window.removeEventListener('resize', placeFieldsetOnFrame);
    window.removeEventListener('scroll', placeFieldsetOnFrame);

    resizeObserver.disconnect();
    if (recursiveIntersectionWatcher) recursiveIntersectionWatcher.stop();
  });

  const handleSelect = (value: AnyOption) => {
    selectedOption.value = value;

    handleClose();
    if (searchRef.value) searchRef.value.focus();
  };

  const handleInputKeydown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        handleOpen();
        break;
      case 'Escape':
        if (isOpen.value) {
          e.preventDefault();
          if (searchRef.value) searchRef.value.focus();
          handleClose();
        }
        break;
      case 'Tab':
        break;
      case 'Shift':
        break;
      case 'ArrowUp':
        selectPreviousOption();
        break;
      case 'ArrowDown':
        selectNextOption();
        break;
      default:
        handleOpen();
        if (!props.isFilteredOnSearch) e.preventDefault();
        break;
    }
  };

  const handleFieldsetKeydown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        (document.activeElement?.previousElementSibling as HTMLElement)?.focus({
          preventScroll: true,
        });
        document.activeElement?.previousElementSibling?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
        break;
      case 'ArrowDown':
        e.preventDefault();
        (document.activeElement?.nextElementSibling as HTMLElement)?.focus({ preventScroll: true });
        document.activeElement?.nextElementSibling?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
        break;
      default:
        break;
    }
  };

  const handleLabelEscape = () => {
    handleClose();
    if (searchRef.value) searchRef.value.focus();
  };

  const handleBlur = () => {
    setTimeout(() => {
      const containers = [wrapperRef.value, fieldsetRef.value];
      const active = document.activeElement;

      const contained = containers.some((container) => container?.contains(active));

      if (!contained) {
        handleClose();
      }
    }, 0);
  };

  const handleOpen = () => {
    isOpen.value = true;
    setTimeout(() => {
      placeFieldset();
    }, 0);
  };

  const handleClose = async () => {
    await nextTick();
    if (selectedOption.value?.label) searchValue.value = selectedOption.value.label;
    isOpen.value = false;
  };

  function selectPreviousOption() {
    if (!options.value || !selectedOption.value) return;
    const currentIndex = options.value.findIndex((option) => {
      return option.label === selectedOption.value?.label;
    });
    if (currentIndex <= 0) return;
    selectedOption.value = options.value[currentIndex - 1];
  }

  function selectNextOption() {
    if (!options.value || !selectedOption.value) return;
    const currentIndex = options.value.findIndex((option) => {
      return option.label === selectedOption.value?.label;
    });

    if (currentIndex >= options.value.length - 1) return;
    selectedOption.value = options.value[currentIndex + 1];
  }

  function optionsDisplayedFilter(option: AnyOption) {
    if (props.isFilteredOnSearch) {
      return option.label.toLowerCase().includes(searchValue.value?.toLowerCase() || '');
    } else {
      return true;
    }
  }

  function placeFieldsetOnFrame() {
    if (!tick) {
      requestAnimationFrame(() => {
        placeFieldset();
        tick = false;
      });
      tick = true;
    }
  }

  function placeFieldset() {
    if (fieldsetRef.value && wrapperRef.value) {
      fieldsetRef.value.hidden = true;
      placeDropdown(fieldsetRef.value, wrapperRef.value, 16);
      fieldsetRef.value.hidden = false;
    }
  }

  defineExpose({ searchRef });
</script>

<template>
  <div
    :id="`custom-search-select-${index}`"
    ref="wrapperRef"
    class="custom-search-select-wrapper relative bg-grey-lightest h-full"
    :class="[
      { 'standalone grow': standalone },
      { 'cursor-not-allowed text-grey-middle': disabled },
    ]"
  >
    <input
      :id="`custom-search-select-input-${index}`"
      ref="searchRef"
      v-model="searchValue"
      :type="type"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      class="bg-grey-lightest pl-4 pr-2 py-2 rounded-md grow w-full text-inherit whitespace-nowrap text-ellipsis overflow-hidden"
      :class="{ 'cursor-pointer caret-transparent': !props.isFilteredOnSearch }"
      @click="handleOpen"
      @keydown="handleInputKeydown"
      @blur="handleBlur"
    />
    <fieldset
      v-if="isOpen && options && options?.filter(optionsDisplayedFilter).length"
      ref="fieldsetRef"
      class="custom-search-input-fieldset bg-grey-lightest z-10 overflow-y-auto min-w-0 overflow-x-hidden"
      @keydown="handleFieldsetKeydown"
    >
      <div class="snap-container p-1 snap-y">
        <label
          v-for="option in options?.filter(optionsDisplayedFilter)"
          :key="option.label"
          :for="`custom-search-select-radio-${index}-${option.value}`"
          tabindex="0"
          class="flex flex-nowrap p-1 rounded-md hover:bg-grey-light/25 focus:bg-grey-light/25 snap-start"
          :class="{ 'bg-grey-light': option.value === selectedOption?.value }"
          @click.stop="() => handleSelect(option)"
          @keyup.enter.prevent="() => handleSelect(option)"
          @blur="handleBlur"
          @keydown.esc.prevent="handleLabelEscape"
          ><input
            :id="`custom-search-select-radio-${index}-${option.value}`"
            type="radio"
            :value="option.value"
            :checked="option.value === selectedOption?.value"
            :disabled="disabled || option.disabled"
            tabindex="-1"
            class="sr-only"
          />
          <span
            class="whitespace-nowrap text-ellipsis overflow-hidden"
            :class="[{ 'ml-[14px]': standalone }, { 'ml-[8px]': !standalone }]"
            >{{ option.label }}</span
          ></label
        >
      </div>
    </fieldset>
  </div>
</template>

<style scoped lang="css">
  /* Validity */
  .standalone input:invalid {
    border: 2px solid var(--red-error);
  }
  .standalone input:placeholder-shown {
    border: none;
  }
  .standalone input:focus-within {
    border: none;
  }
  .standalone input[required] {
    border-left: 6px solid var(--red-error);
  }
  .standalone input:valid {
    border-left: 6px solid var(--green-valid);
  }

  /* Radius */
  .custom-search-select-wrapper {
    border-radius: 0.5rem;
  }

  .custom-search-select-wrapper:has(.dropdown-above) {
    border-radius: 0 0 0.5rem 0.5rem;
  }

  .custom-search-select-wrapper:has(.dropdown-below) {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  .dropdown-above {
    border-radius: 0.5rem 0.5rem 0 0;
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .dropdown-below {
    border-radius: 0 0 0.5rem 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
</style>
